/* eslint-disable camelcase */
import { useCallback } from 'react';

import type { SxProps } from '@mui/material';
import type { MRT_TableInstance as MRTTableInstance, MRT_ColumnDef } from 'material-react-table';
import {
  MRT_GlobalFilterTextField as MRTGlobalFilterTextField,
  MRT_ToggleDensePaddingButton as MRTToggleDensePaddingButton,
} from 'material-react-table';

import { Box } from '@/ui/layout';
import { IconButton } from '@/ui/icon-button/icon-button';
import { Switch } from '@/ui/switch/switch';
import ExportButton from './export-button';
import { ShowHideColumnsButton } from './show-hide-columns-button';
import JSONExportButton from './json-export-button';
import FilterMap from './filter-map';
import FilterDataToExtent from './filter-data-extent';
import type { DataTableRow } from './data-table-types';
import { useToolbarActionMessage } from './hooks/useToolbarActionMessage';
import type { TypeFeatureInfoEntry } from '@/api/types/map-schema-types';
import { CONST_LAYER_TYPES } from '@/api/types/layer-schema-types';
import type { SxStyles } from '@/ui/style/types';
import { ClearFiltersIcon, ZoomToFilteredExtentIcon } from '@/ui/icons';
import { useStoreAppShowUnsymbolizedFeatures } from '@/core/stores/states/app-state';
import { useLayerController } from '@/core/controllers/use-controllers';
import { logger } from '@/core/utils/logger';

/** Properties for the TopToolbar component. */
// GV: Disabled prop-types for this to work. From the react 19 ugprade guide it seems that prop-types are deprecated.
interface TopToolbarProps<TData extends DataTableRow> {
  /** Classes or styles for the component. */
  sxClasses: SxStyles;

  /** The path for the current layer being processed. */
  layerPath: string;

  /** Translation function for internationalization. */
  t: (key: string) => string;

  /** The current global filter value. */
  globalFilter: string | null;

  /** Utility functions provided by the table instance. */
  useTable: {
    resetColumnFilters: () => void;
    getFilteredRowModel: () => { rows: Array<{ original: TData }> };
  } | null;

  /** Column definitions for the table. */
  columns: MRT_ColumnDef<DataTableRow, unknown>[];

  /** The data object containing features for the table. */
  data: {
    features?: TypeFeatureInfoEntry[] | null;
  };

  /** The Material React Table instance. */
  table: MRTTableInstance<DataTableRow>;

  /** The count of features before any filters are applied. */
  unfilteredFeaturesCount?: number;

  /** The count of features before extent filtering and symbol visibility are applied. */
  preExtentFeaturesCount: number;

  /** Whether the table is currently filtered to the map extent. */
  filterDataToExtent: boolean;
}

/**
 * Checks if a filter value is active (non-empty).
 *
 * NOTE: No trace logging — this is a pure predicate function called
 * multiple times per render cycle and would create excessive log noise.
 *
 * @param value - The filter value to check
 * @returns Whether the filter value is active (not undefined, null, or empty string)
 */
function isFilterValueActive(value: unknown): boolean {
  if (Array.isArray(value)) {
    return value.some((v) => v !== undefined && v !== null && v !== '');
  }
  return value !== undefined && value !== null && value !== '';
}

/**
 * Creates the top toolbar for the data table with filters, search, and export controls.
 *
 * @param props - Properties defined in TopToolbarProps interface
 * @returns The top toolbar element
 */
function TopToolbar(props: TopToolbarProps<DataTableRow>): JSX.Element {
  // Log
  logger.logTraceRender('components/data-table/top-toolbar');

  const {
    sxClasses,
    layerPath,
    t,
    globalFilter,
    useTable,
    columns,
    data,
    table,
    unfilteredFeaturesCount,
    preExtentFeaturesCount,
    filterDataToExtent,
  } = props;

  const showUnsymbolizedFeatures = useStoreAppShowUnsymbolizedFeatures();
  const layerController = useLayerController();

  // Get toolbar message
  const toolbarMessage = useToolbarActionMessage({
    data: data,
    layerPath,
    tableInstance: table,
    columnFilters: table.getState().columnFilters,
    globalFilter: table.getState().globalFilter ?? '',
    showUnsymbolizedFeatures: showUnsymbolizedFeatures,
    unfilteredFeaturesCount: unfilteredFeaturesCount || 0,
    preExtentFeaturesCount: preExtentFeaturesCount,
    filterDataToExtent: filterDataToExtent,
  });

  // ESRI Dynamic layer data does not include geometry and can't be filtered to extent
  const isEsriDynamic = data.features?.[0]?.geoviewLayerType === CONST_LAYER_TYPES.ESRI_DYNAMIC;

  // The final filtered row model includes column, global, and upstream filters
  const hasFilteredRows = table.getFilteredRowModel().rows.length > 0;

  // Check if there are any active filters (column filters OR global search filter)
  const hasActiveFilters =
    table.getState().columnFilters?.some((filter) => isFilterValueActive(filter.value)) ||
    isFilterValueActive(table.getState().globalFilter);

  // #region Handlers

  /**
   * Handles clearing all active filters (column filters and global search filter).
   */
  const handleClearFilters = useCallback((): void => {
    // Check filters at runtime instead of using closure
    const columnFiltersActive = table.getState().columnFilters?.some((filter) => isFilterValueActive(filter.value));
    const globalFilterActive = isFilterValueActive(table.getState().globalFilter);

    if (columnFiltersActive || globalFilterActive) {
      useTable?.resetColumnFilters();
      table.setGlobalFilter('');
    }
  }, [useTable, table]);

  /**
   * Handles zooming to the extent of the currently filtered rows, or the full layer extent when no filter is active.
   */
  const handleZoomToFilteredExtent = useCallback((): void => {
    const filteredRows = table.getFilteredRowModel().rows;

    if (filteredRows.length === 0) return;

    // Zoom to the currently filtered rows' extent, or the full layer extent when no filter is active
    const zoomPromise = hasActiveFilters
      ? layerController.zoomToFeaturesExtent(
          layerPath,
          filteredRows.map((row) => row.original.gvFeature)
        )
      : layerController.zoomToLayerExtent(layerPath);

    zoomPromise.catch((error: unknown) => {
      // Log
      logger.logPromiseFailed('in handleZoomToFilteredExtent in TopToolbar', error);
    });
  }, [hasActiveFilters, layerController, layerPath, table]);

  // #endregion

  return (
    <Box
      className="data-table-top-toolbar"
      role="region"
      aria-label={t('dataTable.tableControls')}
      sx={[sxClasses.toolbarContainer, sxClasses.dataTableTopToolbar] as SxProps}
    >
      <Box sx={sxClasses.toolbarRow}>
        <Box component="p" role="status" className="filter-results-summary" sx={sxClasses.selectedRows}>
          {toolbarMessage}
        </Box>
        <Box>
          <form role="search" onSubmit={(e) => e.preventDefault()} aria-label={t('dataTable.searchInputLabel')}>
            <MRTGlobalFilterTextField className="buttonOutline" table={table} />
          </form>
        </Box>
      </Box>
      <Box sx={sxClasses.toolbarRow}>
        <Box>
          <IconButton
            className="buttonOutline"
            aria-label={t('dataTable.zoomToFilteredExtent')}
            aria-disabled={!hasFilteredRows}
            color="primary"
            onClick={handleZoomToFilteredExtent}
          >
            <ZoomToFilteredExtentIcon />
          </IconButton>
          <FilterMap layerPath={layerPath} isGlobalFilterOn={!!globalFilter?.length} />
          {!isEsriDynamic && <FilterDataToExtent layerPath={layerPath} />}
        </Box>
        <Box sx={sxClasses.toolbarControls}>
          <Switch
            checked={table.getState().showColumnFilters}
            onChange={() => table.setShowColumnFilters(!table.getState().showColumnFilters)}
            size="small"
            label={
              table.getState().showColumnFilters
                ? `${t('general.hide')} ${t('dataTable.filterToggle')}`
                : `${t('general.show')} ${t('dataTable.filterToggle')}`
            }
            sx={sxClasses.filterMap}
          />

          <Box sx={sxClasses.toolbarButtonGroup}>
            <IconButton
              className="buttonOutline"
              aria-label={t('dataTable.clearFilters')}
              aria-disabled={!hasActiveFilters}
              color="primary"
              onClick={handleClearFilters}
            >
              <ClearFiltersIcon />
            </IconButton>

            {/* Override column pinning options */}
            <ShowHideColumnsButton table={table} />
            <MRTToggleDensePaddingButton className="buttonOutline" table={table} aria-pressed={table.getState().density === 'compact'} />

            {/* Export Buttons */}
            {useTable?.getFilteredRowModel()?.rows ? (
              <ExportButton layerPath={layerPath} rows={useTable.getFilteredRowModel().rows.map((row) => row.original)} columns={columns}>
                <JSONExportButton
                  rows={useTable.getFilteredRowModel().rows.map((row) => row.original)}
                  features={data.features as TypeFeatureInfoEntry[]}
                  layerPath={layerPath}
                />
              </ExportButton>
            ) : undefined}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export default TopToolbar;
